package com.kinoshazam.screens.search.medium

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import com.kinoshazam.uiKit.components.videoItem.MediumVideoCardWidthCompact
import com.kinoshazam.uiKit.components.videoItem.mediumBannerSkeletons
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.kinoshazam.screens.details.medium.DialogContent
import com.kinoshazam.screens.search.components.IdentifyWandButton
import com.kinoshazam.screens.search.components.SearchModeSwitcher
import com.kinoshazam.uiKit.components.button.OutlinedCircleIconButton
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.videoItem.MediumVideoItem
import com.kinoshazam.uiKit.components.videoItem.MovieCastItem
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.theme.BoldLarge
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.ErrorView
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.arrow_right
import kinoshazam.resources.generated.resources.error
import kinoshazam.resources.generated.resources.identify_empty
import kinoshazam.resources.generated.resources.identify_found
import kinoshazam.resources.generated.resources.identify_hint
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.repeat
import kinoshazam.resources.generated.resources.right
import kinoshazam.resources.generated.resources.search_actors
import kinoshazam.resources.generated.resources.search_empty
import kinoshazam.resources.generated.resources.search_found
import kinoshazam.resources.generated.resources.search_hint
import kinoshazam.resources.generated.resources.search_videos
import models.Actor
import models.PersonPreview
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlinx.coroutines.delay
import presentation.screens.search.SearchMVIAction
import presentation.screens.search.SearchState

@Composable
fun MediumSearchContent(
    state: SearchState,
    onAction: (SearchMVIAction) -> Unit,
) {
    DialogContent(
        onClose = { onAction(SearchMVIAction.BackClicked) },
        dialogWidth = 640.dp,
        contentAlignment = Alignment.Center,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 24.dp, vertical = 28.dp),
        ) {
            SearchBar(
                query = state.query,
                identifyQuery = state.identifyQuery,
                isLoading = state.isLoading,
                identifyEnabled = state.identifyEnabled,
                identifyMode = state.identifyMode,
                onQueryChanged = { onAction(SearchMVIAction.OnQueryChanged(it)) },
                onIdentifyQueryChanged = { onAction(SearchMVIAction.OnIdentifyQueryChanged(it)) },
                onSubmit = { onAction(SearchMVIAction.SubmitSearch) },
                onToggleIdentify = { onAction(SearchMVIAction.ToggleIdentifyMode) },
            )

            when {
                state.isError -> {
                    Spacer(Modifier.height(24.dp))
                    ErrorView(
                        textError = stringResource(Res.string.error),
                        textAction = stringResource(Res.string.repeat),
                        onAction = { onAction(SearchMVIAction.Reload) },
                    )
                }

                state.videos != null -> {
                    Spacer(Modifier.height(28.dp))
                    ResultsSection(state = state, onAction = onAction)
                }

                state.isLoading -> {
                    Spacer(Modifier.height(28.dp))
                    LazyRow(
                        horizontalArrangement = Arrangement.spacedBy(12.dp),
                    ) {
                        mediumBannerSkeletons(count = 4, width = MediumVideoCardWidthCompact)
                    }
                }
            }
        }
    }
}

@Composable
private fun SearchBar(
    query: String,
    identifyQuery: String,
    isLoading: Boolean,
    identifyEnabled: Boolean,
    identifyMode: Boolean,
    onQueryChanged: (String) -> Unit,
    onIdentifyQueryChanged: (String) -> Unit,
    onSubmit: () -> Unit,
    onToggleIdentify: () -> Unit,
) {
    val activeQuery = if (identifyMode) identifyQuery else query
    val canSubmit = activeQuery.isNotBlank() && !isLoading
    val searchFocus = remember { FocusRequester() }
    val identifyFocus = remember { FocusRequester() }
    LaunchedEffect(identifyMode) {
        delay(420)
        if (identifyMode) {
            runCatching { identifyFocus.requestFocus() }
        } else {
            runCatching { searchFocus.requestFocus() }
        }
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Top,
        horizontalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        SearchModeSwitcher(
            identifyMode = identifyMode,
            identifyEnabled = identifyEnabled,
            modifier = Modifier.weight(1f),
            wandSize = 52.dp,
            titleContent = {
                MediumModeField(
                    value = query,
                    placeholder = stringResource(Res.string.search_hint),
                    isIdentifyField = false,
                    canSubmit = query.isNotBlank() && !isLoading && !identifyMode,
                    onValueChanged = onQueryChanged,
                    onSubmit = onSubmit,
                    focusRequester = searchFocus,
                )
            },
            identifyContent = {
                MediumModeField(
                    value = identifyQuery,
                    placeholder = stringResource(Res.string.identify_hint),
                    isIdentifyField = true,
                    canSubmit = canSubmit,
                    onValueChanged = onIdentifyQueryChanged,
                    onSubmit = onSubmit,
                    focusRequester = identifyFocus,
                )
            },
            wand = {
                IdentifyWandButton(
                    selected = identifyMode,
                    onClick = onToggleIdentify,
                    size = 52.dp,
                )
            },
        )

        OutlinedCircleIconButton(
            painter = painterResource(Res.drawable.arrow_right),
            onClick = onSubmit,
            enabled = activeQuery.isNotBlank(),
            isLoading = isLoading,
            size = 52.dp,
            contentDescription = "Search",
        )
    }
}

@Composable
private fun MediumModeField(
    value: String,
    placeholder: String,
    isIdentifyField: Boolean,
    canSubmit: Boolean,
    onValueChanged: (String) -> Unit,
    onSubmit: () -> Unit,
    focusRequester: FocusRequester? = null,
) {
    val fieldShape = if (isIdentifyField) RoundedCornerShape(26.dp) else CircleShape
    BasicTextField(
        value = value,
        onValueChange = onValueChanged,
        singleLine = !isIdentifyField,
        minLines = 1,
        maxLines = if (isIdentifyField) 8 else 1,
        textStyle = BoldMedium.copy(
            color = UiKitTheme.colors.color_2,
            textAlign = if (isIdentifyField) TextAlign.Start else TextAlign.Center,
        ),
        cursorBrush = SolidColor(UiKitTheme.colors.color_1),
        keyboardOptions = KeyboardOptions(imeAction = ImeAction.Search),
        keyboardActions = KeyboardActions(
            onSearch = { if (canSubmit) onSubmit() }
        ),
        modifier = Modifier
            .fillMaxWidth()
            .then(if (focusRequester != null) Modifier.focusRequester(focusRequester) else Modifier)
            .then(
                if (!isIdentifyField) {
                    Modifier.onPreviewKeyEvent { event ->
                        if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                        val isEnter = event.key == Key.Enter || event.key == Key.NumPadEnter
                        if (isEnter && canSubmit) {
                            onSubmit()
                            true
                        } else {
                            false
                        }
                    }
                } else {
                    Modifier
                }
            )
            .then(
                if (isIdentifyField) Modifier.heightIn(min = 52.dp) else Modifier.height(52.dp)
            )
            .then(
                if (isIdentifyField) {
                    Modifier.background(UiKitTheme.colors.color_3, fieldShape)
                } else {
                    Modifier
                }
            )
            .border(1.5.dp, UiKitTheme.colors.color_1, fieldShape)
            .padding(
                horizontal = 24.dp,
                vertical = if (isIdentifyField) 14.dp else 0.dp,
            ),
        decorationBox = { innerTextField ->
            Box(
                modifier = Modifier.fillMaxWidth(),
                contentAlignment = if (isIdentifyField) Alignment.TopStart else Alignment.Center,
            ) {
                if (value.isEmpty()) {
                    Text(
                        text = placeholder,
                        style = Medium.copy(
                            textAlign = if (isIdentifyField) TextAlign.Start else TextAlign.Center,
                        ),
                        color = UiKitTheme.colors.color_2.copy(alpha = 0.7f),
                    )
                }
                innerTextField()
            }
        },
    )
}

@Composable
private fun ResultsSection(
    state: SearchState,
    onAction: (SearchMVIAction) -> Unit,
) {
    val videos = state.videos.orEmpty()
    val persons = state.persons.orEmpty()

    if (!state.hasResults) {
        TextLabel(
            state = TextLabelState(
                text = stringResource(
                    if (state.identifyMode) Res.string.identify_empty else Res.string.search_empty
                ),
                color = UiKitTheme.colors.color_2,
                textStyle = Medium,
                textAlign = TextAlign.Center,
            ),
            modifier = Modifier.fillMaxWidth(),
        )
        return
    }

    if (persons.isNotEmpty() && !state.identifyMode) {
        SearchSectionHeader(
            title = stringResource(Res.string.search_actors),
        )
        LazyRow(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            contentPadding = PaddingValues(bottom = 16.dp),
        ) {
            items(persons, key = { it.id }) { person ->
                MovieCastItem(
                    actor = person.toActor(),
                    onClick = { onAction(SearchMVIAction.OnPersonClicked(person.id)) },
                )
            }
        }
    }

    if (videos.isNotEmpty()) {
        SearchSectionHeader(
            title = when {
                state.identifyMode -> stringResource(Res.string.identify_found, videos.size)
                persons.isNotEmpty() -> stringResource(Res.string.search_videos)
                else -> stringResource(Res.string.search_found, videos.size)
            },
        )
        LazyRow(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            contentPadding = PaddingValues(bottom = 8.dp),
        ) {
            items(videos, key = { it.id ?: it.hashCode() }) { video ->
                MediumVideoItem(
                    state = VideoState(
                        id = video.id ?: -1,
                        title = video.title.orEmpty(),
                        image = video.imageUrl,
                        placeHolder = painterResource(Res.drawable.movie),
                        placeHolderColor = UiKitTheme.colors.color_5,
                        onAction = { id ->
                            onAction(SearchMVIAction.OnVideoClicked(id, video.isTvShow))
                        },
                    ),
                    width = 140.dp,
                )
            }
        }
    }
}

@Composable
private fun SearchSectionHeader(title: String) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.padding(bottom = 16.dp),
    ) {
        Image(
            painter = painterResource(Res.drawable.right),
            contentDescription = null,
            colorFilter = ColorFilter.tint(UiKitTheme.colors.color_1),
            modifier = Modifier.size(18.dp),
        )
        Spacer(Modifier.width(8.dp))
        TextLabel(
            state = TextLabelState(
                text = title,
                color = UiKitTheme.colors.color_2,
                textStyle = BoldLarge,
            ),
        )
    }
}

private fun PersonPreview.toActor() = Actor(
    castId = null,
    character = null,
    creditId = null,
    id = id,
    name = name,
    originalName = null,
    profilePath = profilePath,
)
